import { useEffect } from "react";

import { useAuth } from "@accurx/auth";
import NotFound from "NotFound";
import { LayoutController } from "layouts/LayoutController";
import { useDispatch } from "react-redux";
import { Redirect, Switch, generatePath, useLocation } from "react-router-dom";
import { SelectedOrganisationController } from "reduxQuarantine/SelectedOrganisationController";
import { useSelectedOrganisation } from "reduxQuarantine/SelectedOrganisationProvider";
import {
    INTERNAL_ROUTES,
    InternalRoutes,
} from "routes/AuthedRoutes/InternalRoutes/InternalRoutes";

import { AccurxWebProviders } from "app/AccurxWebProviders";
import { actionCreators } from "app/AppActions";
import { SimpleRoute } from "app/AppRoute";
import { Home } from "app/Home";
import { ManageOrgRedirects } from "app/ManageOrgRedirects";
import { AccountPageView } from "app/account/AccountPageView";
import { FlemingAnalyticsTracker } from "app/analytics";
import { CookiesModal } from "app/cookies/CookiesModal";
import { GuardedRoute } from "app/guardedRoutes/GuardedRoute";
import {
    ONBOARDING_ROUTES,
    OnboardingRoutesSection,
} from "app/guardedRoutes/routes/OnboardingRoutesSection";
import { useFlemingLoggedInAnalytics } from "app/sessionAnalytics/useFlemingLoggedInAnalytics";
import { SLIDEOUT_PORTAL_ID } from "app/sharedComponents/SlideOut";
import { SelectedAllowedOrganisationProvider } from "app/workspace/context/SelectedAllowedOrganisationProvider";
import { ROUTES } from "shared/Routes";

import { AUTHED_REDIRECTS, PostAuthedRedirects } from "./AuthedRedirects";

export const AuthedRoutes = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const analyticsLoggedInProps = useFlemingLoggedInAnalytics();

    const { user } = useAuth();
    const { selectedOrgId: workspaceId } = useSelectedOrganisation();

    const params = new URLSearchParams(location.search);
    const externalReferrer = params.get("externalreferrer");
    const isEmbeddedParam = params.has("embed");

    const showCookiesModal = user.isLoggedIn
        ? user?.onboarding?.hasAcceptedCookies === null
        : false;

    useEffect(() => {
        isEmbeddedParam && dispatch(actionCreators.setEmbeddedMode(true));
    }, [dispatch, isEmbeddedParam]);

    useEffect(() => {
        if (
            user.isLoggedIn &&
            externalReferrer === "EPRQuickLaunch" &&
            sessionStorage.EPRQuickLaunchTracked === undefined
        ) {
            FlemingAnalyticsTracker.trackPageLoadFromMedicalRecordSystem({
                ...analyticsLoggedInProps,
                userIsLoggedIn: true,
            });
            // Flag in session storage so we don't re-trigger this event if page is reloaded
            sessionStorage.EPRQuickLaunchTracked = true;
        }
    }, [user, externalReferrer, analyticsLoggedInProps]);

    return (
        <>
            <SelectedOrganisationController />
            <Switch>
                <SimpleRoute exact path="/" component={Home} />

                {AUTHED_REDIRECTS.map(({ from, to, toParams, exact }) => {
                    const searchParams = getUpdatedSearchParams(
                        location.search,
                        toParams,
                    );

                    return workspaceId ? (
                        <GuardedRoute
                            key={from}
                            path={from}
                            exact={exact}
                            requires={{
                                authenticationStatus: "LoggedIn",
                                onboarded: true,
                            }}
                        >
                            <Redirect
                                to={{
                                    pathname: generatePath(to, { workspaceId }),
                                    search: searchParams,
                                }}
                            />
                        </GuardedRoute>
                    ) : (
                        <SimpleRoute key={from} path={from} exact={exact}>
                            <Redirect to={ROUTES.joinOrganisation} />
                        </SimpleRoute>
                    );
                })}

                {/* 10/03/2025 - 4,950,000 events in 60 days*/}
                <SimpleRoute
                    path={["/practices", "/practices/*", "/organisations/*"]}
                >
                    <ManageOrgRedirects />
                </SimpleRoute>

                <SimpleRoute path={[ROUTES.postlogin, ROUTES.postSsoRedirect]}>
                    <PostAuthedRedirects />
                </SimpleRoute>

                <AccurxWebProviders
                    workspaceId={workspaceId}
                    is2FAed={user.isLoggedIn && user.is2FAed}
                >
                    <SelectedAllowedOrganisationProvider>
                        <LayoutController>
                            <Switch>
                                <SimpleRoute path={ONBOARDING_ROUTES}>
                                    <OnboardingRoutesSection />
                                </SimpleRoute>

                                <GuardedRoute
                                    requires={{
                                        onboarded: true,
                                        authenticationStatus: "LoggedIn",
                                    }}
                                    path={INTERNAL_ROUTES}
                                >
                                    <InternalRoutes />
                                </GuardedRoute>

                                <SimpleRoute path="*">
                                    <NotFound />
                                </SimpleRoute>
                            </Switch>
                        </LayoutController>
                    </SelectedAllowedOrganisationProvider>
                </AccurxWebProviders>
            </Switch>
            <AccountPageView />
            {showCookiesModal && <CookiesModal />}
            <div id={SLIDEOUT_PORTAL_ID}></div>
        </>
    );
};

// Merge the redirect search params on top of existing search params
const getUpdatedSearchParams = (fromParams: string, toParams?: string) => {
    const searchParams = new URLSearchParams(fromParams);
    const redirectParams = new URLSearchParams(toParams);

    redirectParams.forEach((value, key) => {
        searchParams.set(key, value);
    });

    const searchParamsString = searchParams.toString();
    return searchParamsString ?? `?${searchParamsString}`;
};
